import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {
  CancerPlanTypes,
  isLoaded as isLoadedPlans,
  UnassignedCancerPlanTypes,
} from 'ApplicationEntry/models/ProductsRiders/Cancer/CancerPlanTypes';
import {
  CancerRiderCodeAmount,
  isLoaded as isLoadedRiderCodes,
  UnassignedCancerRiderCodes,
} from 'ApplicationEntry/models/ProductsRiders/Cancer/CancerRiderCodes';
import {
  CancerPolicyOption,
  isLoaded as isLoadedPolicyOptions,
  UnassignedCancerPolicyOption,
} from 'ApplicationEntry/models/ProductsRiders/Cancer/PolicyOption';
import {
  isLoaded as isLoadedProducts,
  PackageModel,
  UnassignedPackageModel,
} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import * as RI from '../RiderInformation';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

const riderCodes: RI.GroupRiderCode[] = [
  {
    code: 'HOSP',
    displayName: 'Hosp',
    propertyName: 'hospitalBenefits',
    units: [1, 2, 3, 4],
  },
  {
    code: 'RAD',
    displayName: 'Rad',
    propertyName: 'radiationChemo',
    units: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  },
  {
    code: 'SURG',
    displayName: 'Surg',
    propertyName: 'surgicalBenefits',
    units: [1, 2, 3, 4],
  },
  {
    code: 'MISC',
    displayName: 'Misc',
    propertyName: 'miscBenefits',
    units: [0, 1],
  },
  {
    code: 'CID',
    displayName: 'Diag',
    propertyName: 'initialDiagnosis',
    units: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  },

  {
    code: 'ICU',
    displayName: 'ICU',
    propertyName: 'intensiveCare',
    units: [0, 1, 2, 3, 4, 5, 6, 7, 8],
  },
  {
    code: 'CS',
    displayName: 'Screening/Wellness',
    propertyName: 'cancerScreening',
    units: [0, 1, 2, 3, 4],
  },
  {
    code: 'GCCPR',
    displayName: 'GCCPR',
    propertyName: 'gccpr',
    units: [0, 1, 2, 3, 4, 5],
    hidden: true,
  },
  {
    code: 'Continuation of Coverage',
    propertyName: 'contnOfCoverage',
    displayName: '',
    units: [],
    controlType: RI.ControlType.Boolean,
    disabled: true,
    hidden: true,
  },
  {
    code: 'Return of Premium',
    propertyName: 'returnOfPremium',
    displayName: '',
    units: [],
    controlType: RI.ControlType.Boolean,
    disabled: true,
    hidden: true,
  },
];

const riderCodesGVCP3: RI.GroupRiderCode[] = [
  {
    code: 'HOSP',
    displayName: 'Hosp',
    propertyName: 'hospitalBenefits',
    units: [1, 2, 3, 4],
  },
  {
    code: 'RAD',
    displayName: 'Rad',
    propertyName: 'radiationChemo',
    units: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  },
  {
    code: 'SURG',
    displayName: 'Surg',
    propertyName: 'surgicalBenefits',
    units: [1, 2, 3, 4],
  },
  {
    code: 'MISC',
    displayName: 'Misc',
    propertyName: 'miscBenefits',
    units: [0, 1],
  },
  {
    code: 'CID',
    displayName: 'Diag',
    propertyName: 'initialDiagnosis',
    units: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  },

  {
    code: 'ICU',
    displayName: 'ICU',
    propertyName: 'intensiveCare',
    units: [0, 1, 2, 3, 4, 5, 6, 7, 8],
  },
  {
    code: 'WR',
    displayName: 'Screening/Wellness',
    propertyName: 'cancerScreening',
    units: [0, 1, 2, 3, 4],
  },
  {
    code: 'GCCPR',
    displayName: 'GCCPR',
    propertyName: 'gccpr',
    units: [0, 1, 2, 3, 4, 5],
  },
  {
    code: 'Continuation of Coverage',
    propertyName: 'contnOfCoverage',
    displayName: '',
    units: [],
    controlType: RI.ControlType.Boolean,
    disabled: true,
  },
  {
    code: 'Return of Premium',
    propertyName: 'returnOfPremium',
    displayName: '',
    units: [],
    controlType: RI.ControlType.Boolean,
    disabled: true,
  },
];
type CancerProps = {
  packageName: StringUnion;
  plan: StringUnion;
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  planOption: StringUnion;
  cwa: StringUnion;
  section125: boolean;
  replacement: boolean;
  policyNumber: PolicyNumber;
  planOptions: CancerPolicyOption;
  riderInformation: RI.default;
  packageList: PackageModel;
  plantypes: CancerPlanTypes;
  riderCodes: CancerRiderCodeAmount;
  situsState: StringUnion;
  baseUnits: StringUnion;
  coverageTiers: StringUnion;
  continuationOfCoverage: boolean;
  returnOfPremium: boolean;
  jStamp: boolean;
};

export class InvalidGroupCancer {
  public readonly key = 'InvalidGroupCancer';

  constructor(
    public readonly plan: StringUnion,
    public readonly packageName: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly planOption: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly planOptions: CancerPolicyOption,
    public readonly riderInformation: RI.default,
    public readonly packageList: PackageModel,
    public readonly plantypes: CancerPlanTypes,
    public readonly riderCodes: CancerRiderCodeAmount,
    public readonly situsState: StringUnion,
    public readonly baseUnits: StringUnion,
    public readonly coverageTiers: StringUnion,
    public readonly continuationOfCoverage: boolean,
    public readonly returnOfPremium: boolean,
    public readonly jStamp: boolean
  ) {}
}

export class ValidGroupCancer {
  public static createApp({
    plan,
    packageName,
    planType,
    premium,
    calculatedPremium,
    planOption,
    cwa,
    section125,
    replacement,
    policyNumber,
    planOptions,
    riderInformation,
    packageList,
    plantypes,
    riderCodes,
    situsState,
    baseUnits,
    coverageTiers,
    continuationOfCoverage,
    returnOfPremium,
    jStamp,
  }: CancerProps) {
    return this.create(
      plan,
      packageName,
      planType,
      premium,
      calculatedPremium,
      planOption,
      cwa,
      section125,
      replacement,
      policyNumber,
      planOptions,
      riderInformation,
      packageList,
      plantypes,
      riderCodes,
      situsState,
      baseUnits,
      coverageTiers,
      continuationOfCoverage,
      returnOfPremium,
      jStamp
    );
  }

  public static create(
    plan: StringUnion,
    packageName: StringUnion,
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    planOption: StringUnion,
    cwa: StringUnion,
    section125: boolean,
    replacement: boolean,
    policyNumber: PolicyNumber,
    planOptions: CancerPolicyOption,
    riderInformation: RI.default,
    packageList: PackageModel,
    plantypes: CancerPlanTypes,
    riderCodes: CancerRiderCodeAmount,
    situsState: StringUnion,
    baseUnits: StringUnion,
    coverageTiers: StringUnion,
    continuationOfCoverage: boolean,
    returnOfPremium: boolean,
    jStamp: boolean
  ) {
    return isNonEmptyString(packageName) &&
      isNonEmptyString(plan) &&
      planType &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      section125 &&
      replacement &&
      policyNumber &&
      riderInformation.codes.size > 0 &&
      riderInformation.amounts.size > 0 &&
      isLoadedProducts(packageList) &&
      isLoadedPlans(plantypes) &&
      isLoadedRiderCodes(riderCodes) &&
      isLoadedPolicyOptions(planOptions) &&
      isNonEmptyString(situsState) &&
      isNonEmptyString(baseUnits) &&
      isNonEmptyString(coverageTiers) &&
      continuationOfCoverage &&
      returnOfPremium &&
      jStamp
      ? new ValidGroupCancer(
          plan,
          packageName,
          planType,
          premium,
          calculatedPremium,
          planOption,
          cwa,
          section125,
          replacement,
          policyNumber,
          planOptions,
          riderInformation,
          packageList,
          plantypes,
          riderCodes,
          situsState,
          baseUnits,
          coverageTiers,
          continuationOfCoverage,
          returnOfPremium,
          jStamp
        )
      : new InvalidGroupCancer(
          plan,
          packageName,
          planType,
          premium,
          calculatedPremium,
          planOption,
          cwa,
          section125,
          replacement,
          policyNumber,
          planOptions,
          riderInformation,
          packageList,
          plantypes,
          riderCodes,
          situsState,
          baseUnits,
          coverageTiers,
          continuationOfCoverage,
          returnOfPremium,
          jStamp
        );
  }

  public readonly key = 'ValidGroupCancer';
  constructor(
    public readonly packageName: StringUnion,
    public readonly plan: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly planOption: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly planOptions: CancerPolicyOption,
    public readonly riderInformation: RI.default,
    public readonly packageList: Loaded<object[]>,
    public readonly plantypes: Loaded<string[]>,
    public readonly riderCodes: Loaded<Array<{code: string; units: string[]}>>,
    public readonly situsState: StringUnion,
    public readonly baseUnits: StringUnion,
    public readonly coverageTiers: StringUnion,
    public readonly continuationOfCoverage: boolean,
    public readonly returnOfPremium: boolean,
    public readonly jStamp: boolean
  ) {}
}

export type GroupCancer = InvalidGroupCancer | ValidGroupCancer;

export type GroupCancerOptions<TReturn> = ToPartialMatchOptions<GroupCancer, 'key', TReturn>;

export const matchGroupCancer = <TReturn>(options: GroupCancerOptions<TReturn>) => (app: GroupCancer): TReturn => {
  switch (app.key) {
    case 'InvalidGroupCancer':
      return options.InvalidGroupCancer ? options.InvalidGroupCancer(app) : options.default(app);
    case 'ValidGroupCancer':
      return options.ValidGroupCancer ? options.ValidGroupCancer(app) : options.default(app);
  }
};

export const EmptyGroupCancer = new InvalidGroupCancer(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  EmptyPolicyNumber,
  new UnassignedCancerPolicyOption(),
  new RI.default(I.List<string>(), I.List<number>(), riderCodes),
  new UnassignedPackageModel(),
  new UnassignedCancerPlanTypes(),
  new UnassignedCancerRiderCodes(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  false
);

export const changePackage = (packageName: string) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, packageName: NonEmptyString.create(packageName)});

export const changePlan = (plan: string) => (app: GroupCancer) => {
  if (plan === 'GVCP2')
    return ValidGroupCancer.createApp({
      ...app,
      plan: NonEmptyString.create(plan),
      packageName: new EmptyString(),
      riderInformation: new RI.default(I.List<string>(), I.List<number>(), riderCodes),
      returnOfPremium: false,
      continuationOfCoverage: false,
      planOption: new EmptyString(),
    });
  else {
    return ValidGroupCancer.createApp({
      ...app,
      plan: NonEmptyString.create(plan),
      packageName: new EmptyString(),
      riderInformation: new RI.default(I.List<string>(), I.List<number>(), riderCodesGVCP3),
      returnOfPremium: false,
      continuationOfCoverage: false,
      planOption: new EmptyString(),
    });
  }
};

export const changePlanType = (planType: string) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, planType: NonEmptyString.create(planType)});

export const changePolicyOption = (planOption: string) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, planOption: NonEmptyString.create(planOption)});

export const changePremium = (premium: string) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeSection125 = (section125: boolean) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, section125});

export const changeReplacement = (replacement: boolean) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, replacement});

export const changePolicyNumber = (policyNumber: StringUnion) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changePackageList = (packages: PackageModel) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, packageList: packages});

export const changePlanTypes = (plans: CancerPlanTypes) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, plantypes: plans});

export const changePolicyOptions = (planOptionList: CancerPolicyOption) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, planOptions: planOptionList});

export const changeRiderCodeList = (riderCodes: CancerRiderCodeAmount) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, riderCodes});

export const changeSitusState = (situsState: string) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, situsState: NonEmptyString.create(situsState)});

export const changeBaseUnits = (baseUnits: string) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, baseUnits: NonEmptyString.create(baseUnits)});

export const changeContinuationOfCoverage = (continuationOfCoverage: boolean) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, continuationOfCoverage});

export const changeReturnOfPremium = (returnOfPremium: boolean) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, returnOfPremium});

export const changeJStamp = (jStamp: boolean) => (app: GroupCancer) => ValidGroupCancer.createApp({...app, jStamp});

export const changePlanOption = (planOption: string) => (app: GroupCancer) =>
  ValidGroupCancer.createApp({...app, planOption: NonEmptyString.create(planOption)});

export const changePackageItem = (packageItem: object) => (app: GroupCancer) => {
  let riderCodes: RI.GroupRiderCode[] = [];
  if (app.plan.value === 'GVCP2') {
    riderCodes = getAllRiderCodes();
  } else {
    riderCodes = getAllRiderCodesGVCP3();
  }

  let riderAmounts: I.List<number> = app.riderInformation.amounts;
  let riderCodeList: I.List<string> = app.riderInformation.codes;

  riderCodes.forEach((riderCode, index) => {
    let riderCodePropertyName;

    if (packageItem.hasOwnProperty((riderCodePropertyName = (riderCode as RI.GroupRiderCode).propertyName))) {
      const amount =
        packageItem[riderCodePropertyName] === null ? Number.NaN : parseInt(packageItem[riderCodePropertyName], 10);

      const riderInformation = RI.default.changeRiderInformation(
        amount,
        riderCode.code,
        index,
        riderAmounts,
        riderCodeList
      );

      riderCodeList = riderInformation.riderCodes;
      riderAmounts = riderInformation.riderAmounts;
    }
  });

  if (app.plan.value === 'GVCP3') {
    return ValidGroupCancer.createApp({
      ...app,
      returnOfPremium: packageItem['returnOfPremium'] === null ? false : true,
      continuationOfCoverage: packageItem['contnOfCoverage'] === null ? false : true,
      planOption: NonEmptyString.create(packageItem['planOption']),
      baseUnits: NonEmptyString.create(packageItem['hospitalBenefits']),
      coverageTiers: NonEmptyString.create(packageItem['coverageTiers']),
      riderInformation: new RI.default(riderCodeList, riderAmounts, riderCodes),
    });
  } else {
    return ValidGroupCancer.createApp({
      ...app,
      returnOfPremium: packageItem['returnOfPremium'] === null ? false : true,
      continuationOfCoverage: packageItem['contnOfCoverage'] === null ? false : true,
      planOption: NonEmptyString.create(packageItem['planOption']),
      baseUnits: NonEmptyString.create(packageItem['hospitalBenefits']),
      riderInformation: new RI.default(riderCodeList, riderAmounts, riderCodes),
    });
  }
};

export const changeRiderInformation = (riderAmount: number, riderCode: string, index: number) => (app: GroupCancer) => {
  const riderInformation = RI.default.changeRiderInformation(
    riderAmount,
    riderCode,
    index,
    app.riderInformation.amounts,
    app.riderInformation.codes
  );

  return ValidGroupCancer.createApp({
    ...app,
    riderInformation: new RI.default(riderInformation.riderCodes, riderInformation.riderAmounts, riderCodes),
  });
};

export const baseUnits = [1, 2, 3, 4];

export const planOptions = [1, 2, 3];

export const plans = ['GVCP2', 'GVCP3'];

export const getAllRiderCodes = () => riderCodes;

export const getAllRiderCodesGVCP3 = () => riderCodesGVCP3;
